/**
 * Planned Task manifest: embedded list with dialog create and Detail edit/preview.
 *
 * ONE manifest where there were two. A live task, a template task and a Visit exception are one record
 * with a `parent` pointer, so the parent the surface is mounted against decides which fields it shows
 * and what a write means — not the entity. That is why the board's display properties list everything
 * any parent can offer: `startsOn` resolves to nothing on a template row and `visitChange` to nothing
 * off a Visit, each because the row genuinely carries no such field.
 */
import { ActivityEntityName, EntityViewDisplay, SurfaceType } from '@jms/model';
import { PlannedTaskLine } from '@jms/model/entities/planned.work/planned.task/planned.task';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { PLANNED_TASK_ENTITY } from './planned.task.model';
import { plannedTaskChangeTag } from './presentation/planned.task.change.tag';
import { resolvePlannedTaskEvidence, resolvePlannedTaskStartsOn, resolvePlannedTaskTitleAdornment, resolvePlannedTaskVisitChange } from './planned.task.list.presentation';

export const plannedTaskManifest = registerManifest<PlannedTaskLine>({
  entity: PLANNED_TASK_ENTITY,
  slug: 'planned-task',

  presentation: { tags: { visitChange: plannedTaskChangeTag } },

  board: {
    supportedDisplays: [EntityViewDisplay.List],
    defaults: {
      header: {
        title: 'Tasks',
        showSearch: true,
        showPaginator: false,
        showViewOptions: false,
        defaultViewId: `${PLANNED_TASK_ENTITY}-embedded-list`,
      },
      list: {
        displayProperties: { allow: ['startsOn', 'evidence', 'description', 'visitChange'] },
        fixedProperties: ['evidence'],
        propertyPresentations: { startsOn: resolvePlannedTaskStartsOn, evidence: resolvePlannedTaskEvidence, visitChange: resolvePlannedTaskVisitChange },
        titleAdornment: resolvePlannedTaskTitleAdornment,
      },
    },
  },

  surfaces: {
    [SurfaceType.Form]: () => import('./surface/form/planned.task.form.surface').then(module => module.plannedTaskFormSurface),
    [SurfaceType.Detail]: () => import('./surface/detail/planned.task.detail.surface').then(module => module.plannedTaskDetailSurface),
  },

  operations: {
    create: { surface: SurfaceType.Form, presentation: { mode: 'dialog', title: 'New Task', width: 'min(728px, calc(100vw - 40px))', height: 'min(920px, calc(100vh - 48px))', maxHeight: 'calc(100vh - 48px)' } },
    edit: { surface: SurfaceType.Detail },
    preview: { surface: SurfaceType.Detail },
  },

  relations: {
    [ActivityEntityName.PlannedItem]: () => import('./relations/planned.task.items.relation').then(module => module.plannedTaskItemsBoardBuilder),
  },
});
